define(["mw-log/Log", "./Sender", "./DirectSendFunction"], function (Log, Sender, sendEventToServer) {
  "use strict";

  var _exports = {};
  // Copyright 2022-2023 The MathWorks, Inc.

  const hasWindow = function () {
    return typeof window !== 'undefined';
  };
  const getIntegNumberForRelease = function (release) {
    const indexToIntegMap = [1, 3, 2];
    if (release && release.length === 6) {
      const year = parseInt(release.substring(1, 5));
      const aorb = release.charAt(5);
      const index = ((year - 2016) * 2 + (aorb === 'a' ? 0 : 1)) % 3;
      return indexToIntegMap[index];
    }
  };
  const generateSessionKey = function () {
    // This code is copied from mw-utils/Utils.generateUuid which only generates 8 characters.
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      /* jslint bitwise: false */
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : r & 0x3 | 0x8;
      /* jslint bitwise: true */
      return v.toString(16);
    });
  };

  /**
   * Defines class that sends DDUX data directly to the DDUX server
   * @private
   */
  class DirectSender extends Sender {
    /**
     * Constructor - Initializes by storing relevant properties
     *
     * @param properties {object} supplies information needed to communicate with the server
     *        and optional event bundling control parameters
     * @param properties.applicationName the name of the application logging data (required)
     * @param properties.applicationVersion the version of the application logging data (required)
     * @param properties.applicationKey the server API key corresponding to the applicationName (optional, because
     *        keys for known applicationNames are built in)
     * @param properties.sendToDev specifies whether logged data should be send to the devevelopment (dev)
     *        or production (prod) URL. If not specified data goes to prod if the page hostname contains "mathworks."
     *        (optional)
     * @param properties.sendFromWorker if true data will be sent from a web worker
     */
    constructor(properties) {
      super();
      Log.assert(properties.applicationName, 'applicationName must be supplied for direct send');
      Log.assert(properties.applicationVersion, 'applicationVersion must be supplied for direct send');
      this._sendProperties = {
        applicationName: properties.applicationName,
        applicationVersion: properties.applicationVersion,
        applicationKey: properties.applicationKey,
        sessionKey: generateSessionKey(),
        postUrl: DirectSender.getPostUrlFromQueryParameter(hasWindow() ? window.location.href : '') || DirectSender.getPostUrlFromPropertiesAndHostname(properties, hasWindow() ? window.location.hostname : ''),
        mockFetch: properties.mockFetch
      };
      if (properties.sendFromWorker && hasWindow() && window.Worker) {
        this._worker = new window.Worker('../../main/DirectSendWorker.js', {
          type: 'module'
        });
        this._worker.postMessage({
          action: 'setup',
          sendProperties: this._sendProperties
        });
      }
    }
    stop() {
      // Nothing to do
    }
    sendEvent(eventType, event) {
      if (this._worker && !this._sendProperties.mockFetch) {
        this._worker.postMessage({
          action: 'send',
          eventType: eventType,
          event: event
        });
      } else {
        sendEventToServer(eventType, event, this._sendProperties);
      }
    }
    static getPostUrlFromQueryParameter(pageUrl) {
      let queryIndex = pageUrl.indexOf('?');
      if (queryIndex >= 0) {
        queryIndex = pageUrl.indexOf('dduxUrlOverride', queryIndex + 1);
        if (queryIndex > 0) {
          queryIndex = pageUrl.indexOf('=', queryIndex + 1);
          let endIndex = pageUrl.indexOf('&', queryIndex + 1);
          if (endIndex < 0) {
            endIndex = pageUrl.length;
          }
          return pageUrl.substring(queryIndex + 1, endIndex);
        }
      }
      return undefined;
    }
    static getPostUrlFromPropertiesAndHostname(properties, hostname) {
      if (properties.endPointUrl) {
        return properties.endPointUrl;
      }
      let postURL = 'https://udc-service';
      let sendToDev = properties.sendToDev;
      let integNumber;
      if (sendToDev !== false) {
        // If hostname contains "integ" extract the integ number (if present) and send to dev
        const integIndex = hostname.indexOf('integ');
        if (integIndex >= 0) {
          sendToDev = true;
          integNumber = hostname.substring(integIndex + 5, integIndex + 6);
          if (!(integNumber === '1' || integNumber === '2' || integNumber === '3')) {
            integNumber = undefined;
          }
        } else if (!sendToDev) {
          // Send to to dev if hostname contains "dev", "test", "stage" or "perf"
          // of doesn't contain "mathworks." (covers both "mathworks.com" and "mathworks.cn")
          sendToDev = hostname.indexOf('dev') >= 0 || hostname.indexOf('test') >= 0 || hostname.indexOf('stage') >= 0 || hostname.indexOf('perf') >= 0 || !hostname.includes('mathworks.');
        }
        if (sendToDev) {
          integNumber = properties.endPointNumber || integNumber || getIntegNumberForRelease(properties.release) || 1;
          postURL += '-integ' + integNumber;
        }
      }
      postURL += '.mathworks.com/udc/service/v1/events';
      return postURL;
    }
  }
  _exports.default = DirectSender;
  return _exports.default;
});
//# sourceMappingURL=DirectSender.js.map
